import { Alert, Grid, Stack, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { portalAccessKey } from "@/features/account/lib/portal-access";
import { PortalAppearancePanel } from "@/features/account/ui/portal-appearance-panel";
import { ProfilePanel } from "@/features/account/ui/profile-panel";
import { SeasonPanel } from "@/features/account/ui/season-panel";
import { TillAppearancePanel } from "@/features/account/ui/till-appearance-panel";
import { getStaffMe } from "@/features/auth/api/auth-staff";
import {
	applyStaffAccount,
	getAccessToken,
} from "@/features/auth/lib/auth-store";
import { useAuth } from "@/features/auth/lib/use-auth";

export function AccountPage() {
	const { t } = useTranslation();
	const { session, isPortalAdmin, canWorkRequests } = useAuth();

	const meQuery = useQuery({
		queryKey: ["auth-staff-me"],
		queryFn: async () => {
			const token = getAccessToken();
			if (!token) {
				throw new Error("Unauthenticated");
			}
			return getStaffMe(token);
		},
	});

	useEffect(() => {
		if (meQuery.data) {
			applyStaffAccount(meQuery.data);
		}
	}, [meQuery.data]);

	const tags = meQuery.data?.tags ?? session?.tags ?? [];
	const accessKey = portalAccessKey({ isPortalAdmin, canWorkRequests, tags });

	return (
		<Stack gap="md">
			<div>
				<Title order={2}>{t("staff.title")}</Title>
				<Text c="dimmed" size="sm">
					{t("staff.subtitle")}
				</Text>
			</div>

			<Alert
				color={
					accessKey === "accessNone" || accessKey === "accessTillOnly"
						? "yellow"
						: "blue"
				}
			>
				{t(`staff.${accessKey}`)}
			</Alert>

			<Grid gutter="md" align="stretch">
				<Grid.Col span={{ base: 12, md: 5 }} order={{ base: 1, md: 2 }}>
					<SeasonPanel isPortalAdmin={isPortalAdmin} />
				</Grid.Col>

				<Grid.Col span={{ base: 12, md: 7 }} order={{ base: 3, md: 1 }}>
					<ProfilePanel
						account={meQuery.data}
						session={session}
						isPortalAdmin={isPortalAdmin}
						canWorkRequests={canWorkRequests}
						loading={meQuery.isLoading}
						error={meQuery.error}
					/>
				</Grid.Col>

				{isPortalAdmin ? (
					<Grid.Col span={{ base: 12, md: 5 }} order={{ base: 2, md: 4 }}>
						<TillAppearancePanel />
					</Grid.Col>
				) : null}

				<Grid.Col span={{ base: 12, md: 7 }} order={{ base: 4, md: 3 }}>
					<Stack gap="md">
						<PortalAppearancePanel />
					</Stack>
				</Grid.Col>
			</Grid>
		</Stack>
	);
}
